import { Readable } from 'stream'
import { inherits } from 'util'
import { AudioCommand } from './AudioCommand'
import { AudioEncoding } from './AudioEncoding'
import { getNewAudioStreamConfiguration } from './index'
import { getStreamWorker } from '../ws/stream'
import { logger } from '../lib/logging'
import client from '../apollo'
import { SET_AUDIO_STREAM_FORMAT } from '../apollo/actions'

// Downsample for everyone on web by default
const TARGET_SAMPLE_RATE = 16000

function detectAudioWorklet () {
  const OfflineAudioContext = window.OfflineAudioContext
  // Web Audio API Offline Audio Context
  const context = new OfflineAudioContext(1, 4800, 48000)
  return Boolean(context.audioWorklet && typeof context.audioWorklet.addModule === 'function')
}

function AudioStreamRecorder (micStream) {
  Readable.call(this)

  const self = this
  // start stream worker, if it is not already started
  const stream = getStreamWorker()

  const AudioContext = window.AudioContext || window.webkitAudioContext
  const ctx = new AudioContext({ sampleRate: 48000 })

  const source = ctx.createMediaStreamSource(micStream)
  const gainNode = ctx.createGain()

  gainNode.gain.setTargetAtTime(0.5, ctx.currentTime, 0.015)

  source.connect(gainNode)

  if (!detectAudioWorklet()) {
    console.debug('WebAudio not supported, giving up')
    logger.error('WebAudio not supported, giving up')
  }

  this.createAccumulationProcessorRecorder(ctx, gainNode)

  // Must always be at least one (and really, only one) audio track
  const audioTrack = micStream.getAudioTracks()[0]

  const format = {
    channels: 1,
    bitDepth: 16,
    encoding: AudioEncoding.LINEAR16,
    sampleRate: TARGET_SAMPLE_RATE,
    signed: true,
    source: audioTrack.label
  }

  client.mutate({
    mutation: SET_AUDIO_STREAM_FORMAT,
    variables: { audioStreamFormat: format }
  })

  stream.postMessage({
    cmd: AudioCommand.CONFIGURATION,
    // New streamStartTime required, so pass `true` for the second parameter
    config: getNewAudioStreamConfiguration(format, true)
  })

  // STOP RECORDER
  this.stop = function () {
    if (ctx.state === 'closed') {
      return
    }

    try {
      micStream.getTracks()[0].stop()
    } catch (err) {
      console.error('Unable to stop track:', err)
    }

    // TODO: send msg to stream worker, to signal accumulation processor to drain remaining buffer

    source.disconnect()

    try {
      ctx.close()
    } catch (err) {
      console.error('Error closing audio context:', err)
    }

    self.recording = false
    self.push(null)
    stream.postMessage({ cmd: AudioCommand.STOP })
    self.emit('close')
  }
}
inherits(AudioStreamRecorder, Readable)

// Attempts to connect the microphone to an AccumulationProcessor audio worklet that directly sends audio to the
// StreamWorker. If this should fail for any reason, falls back to utilizing the deprecated ScriptProcessorNode
AudioStreamRecorder.prototype.createAccumulationProcessorRecorder = function (ctx, gainNode) {
  Promise.all([
    ctx.audioWorklet.addModule('/audio/AccumulationProcessor.js')
  ]).then(() => {
    const accumulator = new window.AudioWorkletNode(ctx, 'accumulation-processor')
    // Transfer the accumulator's port to the StreamWorker so messages sent by the accumulation processor bypass the
    // main UI thread and go directly to the StreamWorker.
    getStreamWorker().postMessage({
      cmd: AudioCommand.TRANSFER_PORT,
      port: accumulator.port
    }, [accumulator.port])

    console.debug('WebAudio: Utilizing AudioWorklets - With AccumulationProcessor')
    logger.info('WebAudio: Utilizing AudioWorklets - With AccumulationProcessor')
    gainNode.connect(accumulator).connect(ctx.destination)
  })
}

AudioStreamRecorder.prototype._read = function (/* bytes */) {
  // no-op, (flow-control doesn't really work on sound)
}

export default AudioStreamRecorder
